import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, KeyRound, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import logo from '../assets/logo.png';
import ThemeToggle from '../components/ui/ThemeToggle';
import { useConsoleTheme } from '../lib/theme';

const MIN_LEN = 12;

// Client-side validation mirrors the server's rules. Server-side is the
// authoritative gate; this just avoids a round-trip for obvious failures.
function weaknessFor(pw) {
  if (pw.length < MIN_LEN) return `Must be at least ${MIN_LEN} characters`;
  if (!/[A-Z]/.test(pw)) return 'Must include at least 1 uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Must include at least 1 lowercase letter';
  if (!/\d/.test(pw)) return 'Must include at least 1 digit';
  return null;
}

function landingFor(role) {
  if (role === 'nexsav_admin' || role === 'ops') return '/admin';
  return '/portal';
}

export default function ChangePassword() {
  const { user, clearMustChangePassword } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [fieldError, setFieldError] = useState({});
  useConsoleTheme(); // marks <html> with the console theme while mounted

  const clientWeakness = useMemo(() => (next.length > 0 ? weaknessFor(next) : null), [next]);
  const mismatch = confirm.length > 0 && confirm !== next;

  const canSubmit =
    current.length > 0 && next.length >= MIN_LEN && !clientWeakness && !mismatch && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    setFieldError({});
    if (!current) {
      setFieldError({ current_password: 'Enter your current password.' });
      return;
    }
    if (clientWeakness) {
      setFieldError({ new_password: clientWeakness });
      return;
    }
    if (mismatch) {
      setFieldError({ confirm_password: 'Passwords do not match.' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      clearMustChangePassword();
      navigate(landingFor(user?.role), { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.data?.field) {
          setFieldError({ [err.data.field]: err.message });
        } else {
          setServerError(err.message || 'Unable to change password.');
        }
      } else {
        setServerError('Network error, please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full px-5 py-3.5 pl-11 rounded-xl bg-white/[0.04] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.06] transition-all';

  return (
    <div className="portal-skin min-h-screen relative flex flex-col">
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 grain-bg opacity-[0.13]" />
      </div>

      <div className="relative z-[1] flex-1 flex items-center justify-center px-6 py-16">
        <div
          className="w-full max-w-md p-8 md:p-10 rounded-2xl bg-white/[0.025] backdrop-blur-sm border border-white/[0.06] shadow-[0_2px_20px_-4px_rgba(0,0,0,0.3)]"
          data-testid="change-password-form-wrapper"
        >
          <div className="flex flex-col items-center text-center mb-8">
            <img src={logo} alt="NexSav Innovate" data-light-invert className="h-10 w-auto mb-6 opacity-90" />
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/[0.08] mb-5">
              <KeyRound className="w-3 h-3 text-[#B0FC38]" />
              <span className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">Set a new password</span>
            </div>
            <h1 className="text-2xl font-semibold text-white tracking-tight">Welcome to <span className="serif-acc accent-text">NexSav</span>.</h1>
            <p className="text-sm text-gray-500 mt-2">
              Choose a new password before continuing. Must be {MIN_LEN}+ characters with upper, lower, and a digit.
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
            <PasswordField
              id="current"
              label="Current password"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              error={fieldError.current_password}
              inputCls={inputCls}
            />
            <PasswordField
              id="next"
              label="New password"
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              error={fieldError.new_password || clientWeakness}
              inputCls={inputCls}
            />
            <PasswordField
              id="confirm"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              error={fieldError.confirm_password || (mismatch ? 'Passwords do not match' : null)}
              inputCls={inputCls}
            />

            {serverError && (
              <div className="px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-sm text-red-300/90" role="alert">
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="mt-2 inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Update password
                </>
              )}
            </button>
          </form>
        </div>
        {/* Top right corner; absolute so nothing else moves. */}
        <div className="absolute top-[34px] right-6 md:right-8">
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}

function PasswordField({ id, label, value, onChange, autoComplete, error, inputCls }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-gray-400/80 mb-2 tracking-wide">
        {label}
      </label>
      <div className="relative">
        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
        <input
          id={id}
          type="password"
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
        />
      </div>
      {error && (
        <p className="mt-1 text-[11px] text-red-400" data-testid={`${id}-error`}>{error}</p>
      )}
    </div>
  );
}
